import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Minimal Web Speech API typing — the standard lib.dom.d.ts doesn't ship these.
interface SpeechRecognitionAlternativeLike {
	transcript: string;
}
interface SpeechRecognitionResultLike {
	isFinal: boolean;
	0: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionEventLike {
	resultIndex: number;
	results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionLike {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	onresult: ((event: SpeechRecognitionEventLike) => void) | null;
	onerror: ((event: { error: string }) => void) | null;
	onend: (() => void) | null;
	start: () => void;
	stop: () => void;
	abort: () => void;
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

const getSpeechRecognitionCtor = (): SpeechRecognitionConstructor | null => {
	if (typeof window === "undefined") return null;
	const w = window as unknown as {
		SpeechRecognition?: SpeechRecognitionConstructor;
		webkitSpeechRecognition?: SpeechRecognitionConstructor;
	};
	return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

export interface UseSpeechToTextOptions {
	onFinal: (text: string) => void;
	/** Auto-stop listening after this many ms of no new speech. Omit to disable. */
	silenceMs?: number;
	/**
	 * Regex run against each finalized transcript chunk. On match, the matching
	 * phrase is stripped from the chunk (the cleaned remainder still flows to
	 * onFinal), then `onStopWord` is invoked and listening stops.
	 */
	stopWordPattern?: RegExp;
	onStopWord?: () => void;
}

export interface UseSpeechToTextResult {
	supported: boolean;
	listening: boolean;
	interim: string;
	error: string | null;
	start: () => void;
	stop: () => void;
}

/**
 * Dictation for a chat composer, shared by both design assistants.
 *
 * Extracted from SmartProgramChat unchanged when the treatment-config
 * assistant needed the same behaviour — the stop-word and silence handling are
 * fiddly enough that a second copy would have drifted.
 */
export const useSpeechToText = ({
	onFinal,
	silenceMs,
	stopWordPattern,
	onStopWord,
}: UseSpeechToTextOptions): UseSpeechToTextResult => {
	const Ctor = useMemo(() => getSpeechRecognitionCtor(), []);
	const supported = !!Ctor;

	const [listening, setListening] = useState(false);
	const [interim, setInterim] = useState("");
	const [error, setError] = useState<string | null>(null);

	const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
	const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onFinalRef = useRef(onFinal);
	const onStopWordRef = useRef(onStopWord);
	useEffect(() => {
		onFinalRef.current = onFinal;
	}, [onFinal]);
	useEffect(() => {
		onStopWordRef.current = onStopWord;
	}, [onStopWord]);

	const clearSilenceTimer = useCallback(() => {
		if (silenceTimerRef.current) {
			clearTimeout(silenceTimerRef.current);
			silenceTimerRef.current = null;
		}
	}, []);

	const stop = useCallback(() => {
		clearSilenceTimer();
		const rec = recognitionRef.current;
		if (rec) {
			try {
				rec.stop();
			} catch {
				// ignore
			}
		}
	}, [clearSilenceTimer]);

	const start = useCallback(() => {
		if (!Ctor) return;
		setError(null);
		setInterim("");

		const rec = new Ctor();
		rec.continuous = true;
		rec.interimResults = true;
		rec.lang =
			typeof navigator !== "undefined" && navigator.language
				? navigator.language
				: "en-US";

		const resetSilenceTimer = (): void => {
			if (!silenceMs) return;
			if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
			silenceTimerRef.current = setTimeout(() => {
				try {
					rec.stop();
				} catch {
					// ignore
				}
			}, silenceMs);
		};

		rec.onresult = (event): void => {
			let nextFinal = "";
			let nextInterim = "";
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				const transcript = result[0]?.transcript ?? "";
				if (result.isFinal) {
					nextFinal += transcript;
				} else {
					nextInterim += transcript;
				}
			}

			resetSilenceTimer();

			if (nextFinal) {
				let stopWordHit = false;
				if (stopWordPattern && stopWordPattern.test(nextFinal)) {
					stopWordHit = true;
					nextFinal = nextFinal.replace(stopWordPattern, "").trim();
				}
				if (nextFinal) onFinalRef.current(nextFinal);
				if (stopWordHit) {
					// Stop listening first so onStopWord's handler runs cleanly.
					try {
						rec.stop();
					} catch {
						// ignore
					}
					// Defer to next tick so React flushes the setComposer update
					// triggered above before the consumer reads composer state.
					setTimeout(() => {
						onStopWordRef.current?.();
					}, 0);
				}
			}
			setInterim(nextInterim);
		};

		rec.onerror = (event): void => {
			setError(event.error || "unknown");
			setListening(false);
			clearSilenceTimer();
		};

		rec.onend = (): void => {
			setListening(false);
			setInterim("");
			clearSilenceTimer();
		};

		recognitionRef.current = rec;
		try {
			rec.start();
			setListening(true);
			resetSilenceTimer();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
			setListening(false);
		}
	}, [Ctor, silenceMs, stopWordPattern, clearSilenceTimer]);

	// Stop on unmount so the mic doesn't keep running after navigating away.
	useEffect(() => {
		return () => {
			clearSilenceTimer();
			const rec = recognitionRef.current;
			if (rec) {
				try {
					rec.abort();
				} catch {
					// ignore
				}
			}
		};
	}, [clearSilenceTimer]);

	return { supported, listening, interim, error, start, stop };
};
